<template>
  <PageView class="full-page" :no-sub-title-expected="true">
    <TitleView>{{ $t("registerPage.titre") }}</TitleView>

    <ContentView>
      <UserInformationsView
        v-if="currentStep === registerSteps.USER"
        v-model="utilisateur"
        @update:model-value="updateUser"
        @accept-visibility="
          (accept) => (utilisateur.acceptNameVisibilityOnProtectedListing = accept)
        "
      />
      <ProfilSelectionView
        v-if="currentStep === registerSteps.PROFIL"
        v-model="utilisateur.profil"
        @update:model-value="moveProfileOn"
        @go-back="(profil) => updateProfile(profil, registerSteps.USER)"
      />
      <ReferentCompetencesView
        v-if="currentStep === registerSteps.COMPETENCES"
        v-model="utilisateur"
        @go-back="(updatedUser) => updateCompetences(updatedUser, registerSteps.PROFIL)"
        @update:model-value="
          (updatedUser) => updateCompetences(updatedUser, registerSteps.STRUCTURE_SELECTION)
        "
      />
      <FonctionInformationsView
        v-if="currentStep === registerSteps.FONCTION"
        v-model="utilisateur"
        @go-back="(updatedUser) => updateFonction(updatedUser, registerSteps.PROFIL)"
        @update:model-value="
          (updatedUser) => updateFonction(updatedUser, registerSteps.INSCRIPTION)
        "
      />

      <StructureSelectionView
        v-if="
          currentStep === registerSteps.STRUCTURE_SELECTION ||
          currentStep === registerSteps.STRUCTURE_ADDITION
        "
        ref="structureSelection"
        v-model="utilisateur.structure"
        :show-accept-visibility-checkbox="true"
        @update:model-value="(structure) => updateStructure(structure, registerSteps.INSCRIPTION)"
        @go-back="(structure) => updateStructure(structure, registerSteps.COMPETENCES)"
        @tab-index-changed="refreshURLFromCurrentTabIndex"
      />
      <EtablissementSelectionView
        v-if="
          currentStep === registerSteps.ETABLISSEMENT_SELECTION ||
          currentStep === registerSteps.ETABLISSEMENT_ADDITION
        "
        ref="etablissementSelection"
        v-model="utilisateur.etablissement"
        :show-accept-visibility-checkbox="true"
        :input-label="''"
        :button-label="$t('valider')"
        @etablissement-selected="(etablissement) => updateEtablissement(etablissement, '')"
        @go-back="(etablissement) => updateEtablissement(etablissement, registerSteps.PROFIL)"
        @update:model-value="
          (etablissement) => updateEtablissement(etablissement, registerSteps.INSCRIPTION)
        "
      />

      <b-loading v-model="isLoading" is-full-page />

      <b-message v-if="errorMessage" type="is-danger">
        {{ errorMessage }}
        <router-link to="/login" class="button is-primary">
          <strong> {{ $t("login") }}</strong>
        </router-link>
      </b-message>
    </ContentView>
  </PageView>
</template>

<script lang="ts">
import { BLoading, BMessage } from "buefy"

import { defineComponent } from "vue"
import TitleView from "@/views/commons/TitleView.vue"
import ContentView from "@/views/commons/ContentView.vue"
import PageView from "@/views/commons/PageView.vue"
import UserInformationsView from "@/views/login/registration/UserInformationsView.vue"
import ProfilSelectionView from "@/views/login/registration/ProfilSelectionView.vue"
import StructureSelectionView from "@/views/login/registration/StructureSelectionView.vue"
import EtablissementSelectionView from "@/views/login/registration/EtablissementSelectionView.vue"
import ReferentCompetencesView from "@/views/login/registration/ReferentCompetencesView.vue"
import FonctionInformationsView from "@/views/login/registration/FonctionInformationsView.vue"
import { LoginService } from "@/services/rest/login/LoginService"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import {
  EtablissementDTO,
  ProfilUtilisateur,
  StructureDTO,
  UtilisateurRegistrationDTO,
} from "@/model/bean/GeneratedDTOs"
import { REGISTER_STEPS } from "@/model/bean/EnumHelper"

export default defineComponent({
  name: "RegisterPage",
  components: {
    TitleView,
    ContentView,
    PageView,
    UserInformationsView,
    ProfilSelectionView,
    StructureSelectionView,
    EtablissementSelectionView,
    FonctionInformationsView,
    ReferentCompetencesView,
    BMessage,
    BLoading,
  },
  props: {
    registerStep: {
      type: String,
      default: "",
    },
    urlTabIndex: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loginService: LoginService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      registerSteps: REGISTER_STEPS,
      currentStep: REGISTER_STEPS.USER,
      utilisateur: (() => {
        const user = new UtilisateurRegistrationDTO()
        user.etablissement = new EtablissementDTO()
        return user
      })(),
      registrationMessage: "",
      errorMessage: "",
      isLoading: false,
      EtablissementDTO: EtablissementDTO,
    }
  },
  // We need to be sure that we always enter the register page by user form
  // eslint-disable-next-line
  beforeRouteEnter(to: any, _from: any, next: any): void {
    next((vm: any) => {
      // We ts ignore the next line to avoid typescript screaming about moveToStep not existing on element
      //@ts-ignore
      vm.moveToStep(REGISTER_STEPS.USER)
    })
  },
  // eslint-disable-next-line
  beforeRouteUpdate(to: any, from: any, next: any): void {
    if (!to || !to.params || !to.params.registerStep) {
      next()
    }
    const toParamRegisterStep = to.params.registerStep

    // We can't trust the from params registerStep because it has been changed by updateURL
    const fromParamRegisterStep = from.path.split("/")[2]

    const newTabIndex =
      toParamRegisterStep === fromParamRegisterStep ? parseInt(to.params.urlTabIndex) : 0

    const etablissementSelectionElmt = this.$refs.etablissementSelection

    if (etablissementSelectionElmt) {
      // We ts ignore the next line to avoid typescript screaming about needToAddEtablissement not existing on element
      //@ts-ignore
      etablissementSelectionElmt.needToAddEtablissement =
        toParamRegisterStep === REGISTER_STEPS.ETABLISSEMENT_ADDITION
    }

    const structureSelectionElmt = this.$refs.structureSelection

    if (structureSelectionElmt) {
      // We ts ignore the next line to avoid typescript screaming about needToAddStructure not existing on element
      //@ts-ignore
      structureSelectionElmt.needToAddStructure =
        toParamRegisterStep === REGISTER_STEPS.STRUCTURE_ADDITION
      //@ts-ignore
      structureSelectionElmt.setTabIndex(newTabIndex)
    }

    this.currentStep = toParamRegisterStep
    this.errorMessage = ""

    next()
    this.refreshURLFromCurrentTabIndex(newTabIndex)
  },
  mounted(): void {
    window.scrollTo(0, 0)
  },
  methods: {
    /**
     * Refreshes url when tab item changes
     */
    refreshURLFromCurrentTabIndex(newCurrentTabIndex: number): void {
      const params = this.$route.params
      if (newCurrentTabIndex.toString() !== params["urlTabIndex"]) {
        params["urlTabIndex"] = newCurrentTabIndex.toString()
        this.$router.push({ params: params })
      }
    },

    updateUser(user: UtilisateurRegistrationDTO): void {
      this.utilisateur.nom = user.nom
      this.utilisateur.prenom = user.prenom
      this.utilisateur.courriel = user.courriel
      this.utilisateur.motDePasse = user.motDePasse
      this.utilisateur.confirmationMotDePasse = user.confirmationMotDePasse
      this.moveToStep(this.registerSteps.PROFIL)
    },

    moveProfileOn(profil: ProfilUtilisateur): void {
      const step = this.getStepAccordingToProfil(profil) || this.registerSteps.INSCRIPTION
      this.updateProfile(profil, step)
    },

    updateProfile(profil: ProfilUtilisateur, step: string): void {
      this.utilisateur.profil = profil
      this.moveToStep(step)
    },

    updateStructure(struct: StructureDTO, step: string): void {
      this.utilisateur.structure = struct
      this.moveToStep(step)
    },

    updateEtablissement(etablissement: EtablissementDTO, step: string): void {
      this.utilisateur.etablissement = etablissement
      if (step !== "") {
        this.moveToStep(step)
      }
    },

    updateFonction(utilisateur: UtilisateurRegistrationDTO, step: string): void {
      this.utilisateur.grae = utilisateur.grae
      this.utilisateur.fonction = utilisateur.fonction
      this.utilisateur.employeur = utilisateur.employeur
      this.moveToStep(step)
    },

    updateCompetences(utilisateur: UtilisateurRegistrationDTO, step: string): void {
      this.utilisateur.competencesReferentPedagogie = utilisateur.competencesReferentPedagogie
      this.utilisateur.competencesReferentEcologie = utilisateur.competencesReferentEcologie
      this.utilisateur.competencesReferentAe = utilisateur.competencesReferentAe
      this.moveToStep(step)
    },

    getStepAccordingToProfil(profil?: ProfilUtilisateur): string {
      if (!profil) {
        return ""
      }
      let step = ""
      if (profil === "ENSEIGNANT") {
        step = this.registerSteps.ETABLISSEMENT_SELECTION
      } else if (profil === "REFERENT") {
        step = this.registerSteps.COMPETENCES
      } else {
        step = this.registerSteps.FONCTION
      }
      return step
    },

    async moveToStep(nextStep: string): Promise<void> {
      this.currentStep = nextStep
      if (this.currentStep === this.registerSteps.INSCRIPTION) {
        await this.confirm()
      } else {
        this.updateUrl(nextStep)
      }
    },

    updateUrl(nextStep: string): void {
      const params = this.$route.params
      if (nextStep !== params["registerStep"]) {
        params["registerStep"] = nextStep
        this.$router.push({ params: params })
      }
    },

    async confirm(): Promise<void> {
      try {
        this.isLoading = true
        await this.loginService.register(this.utilisateur)
        this.registrationMessage = this.$t("registerPage.mailsent").toString()
        await this.$router.push({
          name: "RegisterPending",
          state: {
            errorMessage: this.errorMessage,
            registrationMessage: this.registrationMessage,
            mail: this.utilisateur.courriel,
            initialResendMode: "none",
          },
        })
      } catch (error) {
        this.errorMessage = typeof error === "string" ? this.$t(error).toString() : String(error)
      } finally {
        this.isLoading = false
      }
    },
  },
})
</script>
